import { useState } from "react";
import { Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { formatRelative } from "@/lib/utils";
import { useDealComments } from "../use-deal-comments";
import { useAddDealComment } from "../use-add-deal-comment";

// Partner-visible deal comment thread. Consumes the bucket-3 hooks already
// living one level up (`features/portal/use-deal-comments.ts`,
// `features/portal/use-add-deal-comment.ts`) — sub-bucket 5 does NOT
// re-implement these; only surfaces them in UI.
//
// Author chip distinguishes partner vs internal authors. Internal admin
// comments (posted via bucket-13 admin-side hook) surface here when the
// admin posts to the same /api/portal/deals/:dealId/comments endpoint with
// `author_type='internal'` — RLS owns visibility, the thread renders
// whatever the GET returns.
//
// Gated `canPost`: only deals with source='deal' show the composer.
// Attribution-only rows have HubSpot as the conversational surface in the
// CRM; commenting on the portal side would mislead partners.

type Props = {
  dealId: string;
  canPost: boolean;
};

export function DealCommentThread({ dealId, canPost }: Props) {
  const { data, isLoading, isError } = useDealComments(dealId);
  const add = useAddDealComment();
  const [body, setBody] = useState("");

  function handleSend() {
    const trimmed = body.trim();
    if (!trimmed || trimmed.length === 0) return;
    add.mutate(
      { dealId, content: trimmed },
      { onSuccess: () => setBody("") },
    );
  }

  return (
    <section>
      <h3 className="text-sm font-medium text-foreground">Comments</h3>

      {isLoading ? (
        <div className="mt-3 space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-2/3" />
        </div>
      ) : isError ? (
        <p className="mt-3 text-sm text-muted-foreground">
          Couldn&rsquo;t load comments. Refresh to try again.
        </p>
      ) : (data ?? []).length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">
          No comments yet.{" "}
          {canPost
            ? "Use the box below to leave a note for the Bookboost team."
            : "Once Bookboost replies, you'll see it here."}
        </p>
      ) : (
        <ul className="mt-3 space-y-3">
          {(data ?? []).map((c) => {
            const isPartner = c.author_type === "partner";
            return (
              <li
                key={c.id}
                className={
                  isPartner
                    ? "rounded-md border bg-card p-3"
                    : "rounded-md border border-accent/30 bg-accent/5 p-3"
                }
                style={c.pending ? { opacity: 0.6 } : undefined}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-foreground">
                    {isPartner ? "You" : "Bookboost team"}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {formatRelative(c.created_at)}
                  </span>
                  {c.pending ? (
                    <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                      sending…
                    </span>
                  ) : null}
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">
                  {c.content}
                </p>
              </li>
            );
          })}
        </ul>
      )}

      {canPost ? (
        <div className="mt-4">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Add context or ask a question…"
            rows={3}
            disabled={add.isPending}
          />
          <div className="mt-2 flex justify-end">
            <Button
              type="button"
              size="sm"
              onClick={handleSend}
              disabled={add.isPending || body.trim().length === 0}
            >
              {add.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              Send
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
